import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, MessageCircle, CalendarDays, Clock } from "lucide-react";
import { CareersNav } from "../components/careers/CareersNav";
import { ApplyForm } from "../components/careers/ApplyForm";
import { Wingman } from "../components/careers/Wingman";
import { Footer } from "../components/Footer";
import { Reveal, SectionHead, EASE } from "../components/Reveal";
import { API, http, fmtDate } from "../lib/api";

const LINES = [{ t: "Your next", cls: "" }, { t: "leadership move", cls: "italic text-emerald-400" }, { t: "starts here.", cls: "" }];

const JobCard = ({ j }) => (
  <Link to={`/careers/${encodeURIComponent(j.job_code)}`} className="group flex h-full flex-col rounded-md border border-white/10 bg-[#111519] p-7 transition-colors duration-500 hover:border-emerald-400/50" data-testid={`job-card-${j.job_code}`}>
    <div className="flex items-start justify-between gap-4">
      <span className="font-mono text-xs tracking-[0.15em] text-emerald-300">{j.job_code}</span>
      <ArrowUpRight className="h-5 w-5 text-zinc-400 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-emerald-300" />
    </div>
    <p className="font-display mt-6 text-3xl leading-tight text-zinc-100">{j.title}</p>
    <p className="mt-2 text-zinc-300">{j.organisation}</p>
    <div className="mt-auto flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-5 mt-8 text-sm text-zinc-400">
      <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Posted {fmtDate(j.posted_on)}</span>
      <span className="inline-flex items-center gap-2"><Clock className="h-4 w-4" /> Apply by {fmtDate(j.apply_by)}</span>
    </div>
  </Link>
);

const Openings = ({ jobs, loading }) => (
  <section id="openings" className="py-24 md:py-32 border-t border-white/10" data-testid="openings-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <SectionHead index="01" label="Current openings" title={<>Active <em className="text-emerald-400">mandates.</em></>} sub="Roles we are currently searching for on behalf of our clients. Open a role to read the full job description and apply." />
      <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {loading && <p className="text-zinc-400">Loading openings...</p>}
        {!loading && jobs.length === 0 && (
          <p className="md:col-span-2 xl:col-span-3 rounded-md border border-white/10 bg-[#111519] p-8 text-zinc-300" data-testid="openings-empty">
            There are no open roles listed right now. Place your CV on record below and we'll reach out when a matching mandate opens.
          </p>
        )}
        {jobs.map((j, i) => (
          <Reveal key={j.id} delay={(i % 3) * 0.08} className="h-full"><JobCard j={j} /></Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TalentPool = ({ onChat }) => (
  <section id="talent-pool" className="relative py-24 md:py-32 bg-[#0d1013] border-t border-white/10" data-testid="talent-pool-section">
    <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
      <div className="lg:col-span-5">
        <SectionHead index="02" label="General talent record" title={<>Don't see a matching <em className="text-emerald-400">role?</em></>} sub="Drop your CV here to place it on record for future openings. Tell us your industry vertical and role, and our search team will match you as new mandates open." />
        <Reveal delay={0.2} className="mt-10 rounded-md border border-emerald-400/30 bg-emerald-500/[0.06] p-6">
          <p className="font-display text-2xl">Prefer to chat?</p>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed">Ani, our recruitment assistant, can take your details and CV in a quick guided conversation.</p>
          <button onClick={onChat} className="mt-5 inline-flex items-center gap-2 text-sm text-emerald-300 hover:text-emerald-200" data-testid="talent-pool-chat-btn">
            <MessageCircle className="h-4 w-4" /> Chat with Ani
          </button>
        </Reveal>
      </div>
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="rounded-md border border-white/10 bg-[#0a0c0e] p-6 sm:p-10">
          <ApplyForm testid="general-cv-form" />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(0);

  useEffect(() => {
    http.get(`${API}/careers/jobs`)
      .then((r) => setJobs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id || loading) return undefined;
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
    return () => clearTimeout(t);
  }, [loading]);

  return (
    <div className="grain relative overflow-x-clip" data-testid="careers-page">
      <CareersNav />
      <main>
        <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28" data-testid="careers-hero">
          <div className="hairline-grid pointer-events-none absolute inset-0" />
          <div className="pointer-events-none absolute -right-40 top-10 h-[480px] w-[480px] rounded-full bg-emerald-500/10 blur-[120px]" />
          <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
            <p className="eyebrow">Careers · PLUK HR</p>
            <h1 className="font-display mt-8 text-[3rem] leading-[0.95] sm:text-7xl lg:text-[6.5rem] font-medium tracking-[-0.02em]" data-testid="careers-headline">
              {LINES.map((l, i) => (
                <span key={l.t} className="block overflow-hidden pb-2">
                  <motion.span className={`block ${l.cls}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease: EASE }}>{l.t}</motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }} className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-zinc-300">
              Explore current mandates with banks, NBFCs, insurers, fintechs and high-growth startups, or place your CV on record with our search team for future openings.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: EASE }} className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#openings" className="group inline-flex items-center gap-3 rounded-full bg-emerald-500 pl-6 pr-2 py-2 text-[15px] font-medium text-[#0a0c0e] hover:bg-emerald-400 transition-colors" data-testid="careers-view-openings-btn">
                View openings
                <span className="grid h-10 w-10 place-items-center rounded-full bg-[#0a0c0e] text-emerald-400"><ArrowDownRight className="h-4 w-4" /></span>
              </a>
              <a href="#talent-pool" className="px-2 py-3 text-[15px] text-zinc-200 hover:text-white underline-offset-4 hover:underline" data-testid="careers-drop-cv-btn">Drop your CV</a>
            </motion.div>
          </div>
        </section>
        <Openings jobs={jobs} loading={loading} />
        <TalentPool onChat={() => setChat((c) => c + 1)} />
      </main>
      <Footer />
      <Wingman openSignal={chat} />
    </div>
  );
}
